import { DiscountTypeEnum } from '../../enum/discount-type.enum';
import { CONFIG_CONTEXT } from '../../enum/config-context.enum';
import {
  ComplementaryProductState,
  CustomerState,
  Fidelity,
  Funding,
  FundingState,
  InstallationState,
  Price,
  PricingState,
  TunnelDataState,
} from './model/tunnel-data-state';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { Customer } from '../../services/kingfisher/customer';
import { FidelityService } from '../../services/fidelity/fidelity.service';
import { stringToDate } from '../../services/helpers/utils';
import { CustomerService } from '../../services/customer.service';
import { CurrentService } from '../../services/current.service';
import { withTimeout } from '../../services/helpers/timeout-helper';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { TunnelComplementaryProduct } from '../../models/configurators-2d/tunnel/common-tunnel-dtos';
import { FeatureSettingsService } from '../../services/feature-settings/feature-settings.service';
import { environment } from 'src/environments/environment';

const initialState: TunnelDataState = {
  mainContext: undefined,
  currentPricing: undefined,
  currentInstallation: undefined,
  currentFunding: undefined,
  currentCustomer: undefined,
  currentDiscount: DiscountTypeEnum.Discount0,
  currentComplementaryProduct: undefined,
  configurationData: new Map<CONFIG_CONTEXT, unknown>(Object.values(CONFIG_CONTEXT).map(key => [key, undefined])),
  installationLoading: false,
  fundingLoading: false,
  fidelityLoading: false,
  pricingLoading: false,
  complementaryProductLoading: false,
};

@Injectable({
  providedIn: 'root',
})
export class TunnelDataStore {
  // Subjects séparés pour chaque domaine de l'état
  private readonly mainContext$ = new BehaviorSubject<CONFIG_CONTEXT | undefined>(initialState.mainContext);
  private readonly currentPricing$ = new BehaviorSubject<PricingState | undefined>(initialState.currentPricing);
  private readonly currentInstallation$ = new BehaviorSubject<InstallationState | undefined>(
    initialState.currentInstallation
  );
  private readonly currentFunding$ = new BehaviorSubject<FundingState | undefined>(initialState.currentFunding);
  private readonly currentCustomer$ = new BehaviorSubject<CustomerState | undefined>(initialState.currentCustomer);
  private readonly currentDiscount$ = new BehaviorSubject<DiscountTypeEnum>(initialState.currentDiscount);
  private readonly currentComplementaryProduct$ = new BehaviorSubject<ComplementaryProductState | undefined>(
    initialState.currentComplementaryProduct
  );
  private readonly configurationData$ = new BehaviorSubject<Map<CONFIG_CONTEXT, unknown>>(
    initialState.configurationData
  );

  // Loading states
  private readonly installationLoading$ = new BehaviorSubject<boolean>(initialState.installationLoading);
  private readonly fundingLoading$ = new BehaviorSubject<boolean>(initialState.fundingLoading);
  private readonly fidelityLoading$ = new BehaviorSubject<boolean>(initialState.fidelityLoading);
  private readonly pricingLoading$ = new BehaviorSubject<boolean>(initialState.pricingLoading);
  private readonly complementaryProductLoading$ = new BehaviorSubject<boolean>(
    initialState.complementaryProductLoading
  );

  private readonly fidelityService = inject(FidelityService);
  private readonly featureSettingsService = inject(FeatureSettingsService);
  private readonly customerService = inject(CustomerService);
  private readonly currentService = inject(CurrentService);

  protected async fidelityEnabled(): Promise<boolean> {
    return this.featureSettingsService.isFidelityEnabled();
  }

  public getCurrentPricing(): Observable<PricingState | undefined> {
    return this.currentPricing$.asObservable();
  }

  public getCurrentInstallation(): Observable<InstallationState | undefined> {
    return this.currentInstallation$.asObservable();
  }

  public getCurrentComplementaryProduct(): Observable<ComplementaryProductState | undefined> {
    return this.currentComplementaryProduct$.asObservable();
  }

  public getMainContext(): Observable<CONFIG_CONTEXT | undefined> {
    return this.mainContext$.asObservable();
  }

  public getCurrentFunding(): Observable<FundingState | undefined> {
    return this.currentFunding$.asObservable();
  }

  public getCurrentCustomer(): Observable<CustomerState | undefined> {
    return this.currentCustomer$.asObservable();
  }

  public getCurrentDiscount(): Observable<DiscountTypeEnum> {
    return this.currentDiscount$.asObservable();
  }

  public getConfigurationData(): Observable<Map<CONFIG_CONTEXT, unknown>> {
    return this.configurationData$.asObservable();
  }

  public getInstallationLoading(): Observable<boolean> {
    return this.installationLoading$.asObservable();
  }

  public getFidelityLoading(): Observable<boolean> {
    return this.fidelityLoading$.asObservable();
  }

  public getComplementaryProductLoading(): Observable<boolean> {
    return this.complementaryProductLoading$.asObservable();
  }

  public getPricingLoading(): Observable<boolean> {
    return this.pricingLoading$.asObservable();
  }

  public getFundingLoading(): Observable<boolean> {
    return this.fundingLoading$.asObservable();
  }

  public getCurrentWithInstallation(): Observable<boolean> {
    return this.currentInstallation$.pipe(map(installation => installation?.withInstallation ?? false));
  }

  public getCurrentMore2Years(): Observable<boolean> {
    return this.currentInstallation$.pipe(map(installation => installation?.more2Years ?? false));
  }

  public getCurrentFundingDownPayment(): Observable<number | undefined> {
    return this.currentFunding$.pipe(map(funding => funding?.downPayment));
  }

  public getLoading(): Observable<boolean> {
    return combineLatest([
      this.installationLoading$,
      this.fundingLoading$,
      this.fidelityLoading$,
      this.pricingLoading$,
    ]).pipe(map(([installation, funding, fidelity, pricing]) => installation || funding || fidelity || pricing));
  }

  public setFidelityLoading(fidelityLoading: boolean): void {
    this.fidelityLoading$.next(fidelityLoading);
  }

  public setComplementaryProductLoading(complementaryProductLoading: boolean): void {
    this.complementaryProductLoading$.next(complementaryProductLoading);
  }

  public setInstallationLoading(loading: boolean): void {
    this.installationLoading$.next(loading);
  }

  public setPricingLoading(loading: boolean): void {
    this.pricingLoading$.next(loading);
  }

  public setFundingLoading(loading: boolean): void {
    this.fundingLoading$.next(loading);
  }

  public setMainContext(mainContext: CONFIG_CONTEXT): void {
    this.mainContext$.next(mainContext);
  }

  public updateCurrentDiscount(newDiscount: DiscountTypeEnum): void {
    this.currentDiscount$.next(newDiscount);
  }

  public setWithInstallation(withInstallation: boolean): void {
    const current = this.currentInstallation$.getValue();
    this.currentInstallation$.next({
      ...current,
      withInstallation,
    });
  }

  public setMoreThan2Years(checked: boolean): void {
    const current = this.currentInstallation$.getValue();
    this.currentInstallation$.next({
      ...current,
      more2Years: checked,
    });
  }

  public updateCurrentPricing(state: PricingState): void {
    const current = this.currentPricing$.getValue();
    this.currentPricing$.next(current ? { ...current, ...state } : state);
  }

  public updateCurrentInstallation(state: InstallationState): void {
    const current = this.currentInstallation$.getValue();
    this.currentInstallation$.next(current ? { ...current, ...state } : state);
  }

  public updateCurrentFunding(state: FundingState): void {
    const current = this.currentFunding$.getValue();
    this.currentFunding$.next(current ? { ...current, ...state } : state);
  }

  public updateCurrentComplementaryProduct(complementaryProduct: TunnelComplementaryProduct): void {
    const current = this.currentComplementaryProduct$.getValue();
    this.currentComplementaryProduct$.next(current ? { ...current, ...complementaryProduct } : complementaryProduct);
  }

  public updateCurrentCustomer(state: CustomerState): void {
    this.customerService.userDatas = state.userData;
    const current = this.currentCustomer$.getValue();
    this.currentCustomer$.next(current ? { ...current, ...state } : state);
  }

  public updateConfigurationData(context: CONFIG_CONTEXT, data: unknown): void {
    const currentMap = this.configurationData$.getValue();
    const currentContextData = currentMap.get(context) as Record<string, unknown> | undefined;

    // Crée une nouvelle Map pour l'immutabilité
    const newMap = new Map(currentMap);
    newMap.set(context, {
      ...(currentContextData ?? {}),
      ...(data as Record<string, unknown>),
    });

    this.configurationData$.next(newMap);
  }

  private async loadCustomerAndWaitForFidelity(customer: Customer): Promise<void> {
    if (await this.fidelityEnabled()) {
      const currentFidelityRaw = await this.fidelityService.getCustomerFidelityCardInfo({
        idKingfisher: customer.kfId,
      });

      this.updateCurrentCustomer({
        userData: customer,
        fidelity: {
          typeCard: currentFidelityRaw.fidelityCard.type,
          cardNumber: currentFidelityRaw.fidelityCard.cardNumber,
          cardIsPaidButNotActiveYet: currentFidelityRaw.fidelityCard.cardIsPaidButNotActiveYet,
          points: currentFidelityRaw.pointInfo.numberOfPoint,
          createdDate: stringToDate(currentFidelityRaw.fidelityCard.creationDate),
          expiredDate: stringToDate(currentFidelityRaw.fidelityCard.finalExpirationDate),
          coupons: currentFidelityRaw.pointInfo.discountsList,
          maxPoints: currentFidelityRaw.pointInfo.maxPointForNextStep,
        } as Fidelity,
      });
    } else {
      this.updateCurrentCustomer({
        userData: customer,
        fidelity: undefined as Fidelity,
      });
    }
  }

  private async loadCustomerWithBackgroundFidelity(customer: Customer): Promise<void> {
    this.updateCurrentCustomer({
      userData: customer,
      fidelity: undefined as Fidelity,
    });

    if ((await this.fidelityEnabled()) && customer) {
      this.setFidelityLoading(true);
      withTimeout(
        this.fidelityService.getCustomerFidelityCardInfo({
          idKingfisher: customer.kfId,
        }),
        20000
      )
        .then(currentFidelityRaw => {
          const currentCustomerValue = this.currentCustomer$.getValue();
          if (currentFidelityRaw && currentCustomerValue) {
            this.updateCurrentCustomer({
              userData: customer,
              fidelity: {
                typeCard: currentFidelityRaw.fidelityCard.type,
                cardNumber: currentFidelityRaw.fidelityCard.cardNumber,
                cardIsPaidButNotActiveYet: currentFidelityRaw.fidelityCard.cardIsPaidButNotActiveYet,
                points: currentFidelityRaw.pointInfo.numberOfPoint,
                createdDate: stringToDate(currentFidelityRaw.fidelityCard.creationDate),
                expiredDate: stringToDate(currentFidelityRaw.fidelityCard.finalExpirationDate),
                coupons: currentFidelityRaw.pointInfo.discountsList,
                maxPoints: currentFidelityRaw.pointInfo.maxPointForNextStep,
              } as Fidelity,
            });
          }
        })
        .catch(e => logError(e))
        .finally(() => {
          this.setFidelityLoading(false);
        });
    }
  }

  public async loadCurrentCustomer(customer: Customer, backgroundFidelity: boolean = true): Promise<void> {
    try {
      if (backgroundFidelity) {
        await this.loadCustomerWithBackgroundFidelity(customer);
      } else {
        await this.loadCustomerAndWaitForFidelity(customer);
      }
    } catch (e) {
      logError(e);
    }
  }

  /**
   * Détacher le client courant est une opération vendeur : le collègue cesse de travailler
   * pour ce client. Sur le site public le « client courant » est l'utilisateur lui-même, et
   * `logout()` y révoque sa session AuthApp : fermer une modale le déconnecterait.
   */
  public resetCurrentCustomer(): void {
    if (environment.vendor) {
      this.customerService.logout();
    }

    this.currentCustomer$.next(undefined);
  }

  public resetCurrentPricing(): void {
    this.currentPricing$.next(undefined);
  }

  public resetCurrentInstallation(): void {
    this.currentInstallation$.next(undefined);
  }

  public resetCurrentFunding(): void {
    this.currentFunding$.next(undefined);

    // Reset aussi les champs liés dans pricing
    const currentPricing = this.currentPricing$.getValue();
    if (currentPricing) {
      this.currentPricing$.next({
        ...currentPricing,
        totalWithFunding: undefined,
        fidelity: undefined,
      });
    }
  }

  public disableCurrentFunding(): void {
    this.fundingLoading$.next(false);
    this.currentFunding$.next({
      downPayment: 0,
      fundingWithInstallation: this.buildEmptyFunding(),
      fundingWithoutInstallation: this.buildEmptyFunding(),
    });

    const currentPricing = this.currentPricing$.getValue();
    if (currentPricing?.totalWithoutFunding) {
      this.currentPricing$.next({
        ...currentPricing,
        totalWithFunding: currentPricing.totalWithoutFunding.actualAmount,
      });
    }
  }

  public disableCurrentInstallation(): void {
    this.installationLoading$.next(false);
    this.currentInstallation$.next({
      withInstallation: false,
      more2Years: false,
      detailInstallation: undefined,
    });

    const currentPricing = this.currentPricing$.getValue();
    if (currentPricing) {
      const emptyPrice = new Price({ baseAmount: 0, actualAmount: 0 });
      this.currentPricing$.next({
        ...currentPricing,
        totalInstallation: emptyPrice,
        delta: emptyPrice,
      });
    }
  }

  private buildEmptyFunding(): Funding {
    return {
      mensuality: 0,
      duration: 0,
      interest: 0,
      total: 0,
      offerTable: [],
      mlData: {
        ml_sofinco: '',
        ml_sanitaire: '',
      },
      txtAssurance: '',
      vac: '',
      code: 'Aucune offre',
    };
  }

  public resetState(resetCustomer: boolean = true): void {
    if (resetCustomer) {
      this.customerService.logout();
    }
    this.currentService.reset();

    // Reset tous les subjects
    this.mainContext$.next(undefined);
    this.currentPricing$.next(undefined);
    this.currentInstallation$.next(undefined);
    this.currentFunding$.next(undefined);
    this.currentCustomer$.next(resetCustomer ? undefined : this.currentCustomer$.getValue());
    this.currentDiscount$.next(DiscountTypeEnum.Discount0);
    this.currentComplementaryProduct$.next(undefined);
    this.configurationData$.next(new Map<CONFIG_CONTEXT, unknown>(Object.values(CONFIG_CONTEXT).map(key => [key, {}])));
    this.installationLoading$.next(false);
    this.fundingLoading$.next(false);
    this.fidelityLoading$.next(false);
    this.pricingLoading$.next(false);
    this.complementaryProductLoading$.next(false);
  }

  public getCurrentState(): Observable<TunnelDataState> {
    return combineLatest({
      mainContext: this.mainContext$,
      currentPricing: this.currentPricing$,
      currentInstallation: this.currentInstallation$,
      currentFunding: this.currentFunding$,
      currentCustomer: this.currentCustomer$,
      currentDiscount: this.currentDiscount$,
      currentComplementaryProduct: this.currentComplementaryProduct$,
      configurationData: this.configurationData$,
      installationLoading: this.installationLoading$,
      fundingLoading: this.fundingLoading$,
      fidelityLoading: this.fidelityLoading$,
      pricingLoading: this.pricingLoading$,
      complementaryProductLoading: this.complementaryProductLoading$,
    });
  }

  public getCurrentStateValue(): TunnelDataState {
    return {
      mainContext: this.mainContext$.getValue(),
      currentPricing: this.currentPricing$.getValue(),
      currentInstallation: this.currentInstallation$.getValue(),
      currentFunding: this.currentFunding$.getValue(),
      currentCustomer: this.currentCustomer$.getValue(),
      currentDiscount: this.currentDiscount$.getValue(),
      currentComplementaryProduct: this.currentComplementaryProduct$.getValue(),
      configurationData: this.configurationData$.getValue(),
      installationLoading: this.installationLoading$.getValue(),
      fundingLoading: this.fundingLoading$.getValue(),
      fidelityLoading: this.fidelityLoading$.getValue(),
      pricingLoading: this.pricingLoading$.getValue(),
      complementaryProductLoading: this.complementaryProductLoading$.getValue(),
    };
  }
}
